/**
 * Material-UI Component Style Overrides & Reusable Component Styles
 *
 * Customizes Material-UI component styles to match the design system.
 * Also provides reusable style objects for manual application in components.
 *
 * @param {Object} theme - Theme customization object
 * @param {Object} theme.colors - Color values from SCSS
 * @param {string} theme.paper - Paper/background color
 * @param {string} theme.heading - Heading text color
 * @param {string} theme.darkTextSecondary - Secondary text color
 * @param {string} theme.divider - Divider color
 * @param {string} theme.backgroundDefault - Default background color
 * @param {Object} theme.customization - Custom theme options
 * @param {number} theme.customization.borderRadius - Border radius value
 *
 * @returns {Object} Object with component overrides and reusable styles
 */
export default function componentStyleOverrides(theme) {
  const isDark = theme.mode === "dark";
  const inputBg = isDark ? theme.colors?.darkLevel1 : theme.colors?.grey50;
  const inputBorder = isDark ? theme.colors?.darkLevel2 : theme.colors?.grey300;
  const borderRadius = theme?.customization?.borderRadius || 6;

  // ==============================|| MATERIAL-UI COMPONENT OVERRIDES ||============================== //
  const componentOverrides = {
    // ==============================|| BUTTON OVERRIDES ||============================== //
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: "8px",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedPrimary: {
          color: isDark ? "#10192e" : theme.colors?.white,
          backgroundColor: theme.colors?.primaryMain,
          "&:hover": {
            backgroundColor: theme.colors?.primaryDark,
            boxShadow: isDark
              ? "0 4px 16px rgba(33, 150, 243, 0.4)"
              : "0 2px 8px rgba(33, 150, 243, 0.2)",
          },
        },
        outlinedPrimary: {
          borderColor: isDark
            ? theme.colors?.primaryMain
            : theme.colors?.primary200,
          color: theme.colors?.primaryMain,
          "&:hover": {
            borderColor: theme.colors?.primaryMain,
            backgroundColor: isDark
              ? `rgba(33, 150, 243, 0.08)`
              : `rgba(33, 150, 243, 0.04)`,
          },
        },
        containedSecondary: {
          color: theme.colors?.white,
          fontWeight: 600,
          fontSize: "0.875rem",
          padding: "8px 24px",
          "&:hover": {
            backgroundColor: theme.colors?.secondaryDark,
          },
        },
        outlinedError: {
          color: theme.colors?.errorMain,
          borderColor: theme.colors?.errorMain,
          "&:hover": {
            backgroundColor: theme.colors?.errorLight,
            borderColor: theme.colors?.errorDark,
          },
        },
        containedWhite: {
          color: theme.textDark,
          backgroundColor: theme.colors?.white,
          border: "none",
          "&:hover": {
            backgroundColor: theme.colors?.grey50,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          },
        },
        outlinedWhite: {
          color: theme.textDark,
          backgroundColor: theme.colors?.white,
          borderColor: theme.colors?.grey200,
          "&:hover": {
            backgroundColor: theme.colors?.grey50,
            borderColor: theme.colors?.grey300,
          },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.75rem",
        },
        sizeLarge: {
          padding: "12px 32px",
          fontSize: "1rem",
        },
      },
    },
    // ==============================|| TOGGLE BUTTON GROUP OVERRIDES ||============================== //
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    // ==============================|| TOGGLE BUTTON OVERRIDES ||============================== //
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: "8px",
          textTransform: "none",
          boxShadow: "none",
          color: theme.textDark,
          backgroundColor: isDark
            ? theme.colors?.darkLevel1
            : theme.colors?.grey50,
          border: `1px solid ${theme.divider}`,
          "&:hover": {
            boxShadow: "none",
            backgroundColor: isDark
              ? `rgba(33, 150, 243, 0.08)`
              : `rgba(33, 150, 243, 0.04)`,
          },
          "&.Mui-selected": {
            color: isDark ? "#10192e" : theme.colors?.white,
            backgroundColor: theme.colors?.primaryMain,
            "&:hover": {
              backgroundColor: theme.colors?.primaryDark,
              boxShadow: isDark
                ? "0 4px 16px rgba(33, 150, 243, 0.4)"
                : "0 2px 8px rgba(33, 150, 243, 0.2)",
            },
          },
          "&.Mui-disabled": {
            color: theme.darkTextSecondary,
            opacity: 0.6,
          },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.75rem",
        },
        sizeLarge: {
          padding: "12px 32px",
          fontSize: "1rem",
        },
      },
    },
    // ==============================|| PAPER OVERRIDES ||============================== //
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: `${borderRadius}px`,
        },
      },
    },
    // ==============================|| CARD OVERRIDES ||============================== //
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "14px",
          boxShadow: "none",
          backgroundColor: theme.paper,
          border: `1px solid ${theme.divider}`,
          "&.MuiCard-borderless": {
            border: "none !important",
            boxShadow: "none !important",
          },
        },
      },
    },
    // ==============================|| CARD COMPONENT OVERRIDES ||============================== //
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "24px 24px 0",
        },
        title: {
          fontSize: "1.25rem",
          fontWeight: 600,
          color: theme.heading,
          lineHeight: 1.2,
        },
        subheader: {
          fontSize: "0.875rem",
          color: theme.darkTextSecondary,
          marginTop: "4px",
        },
        action: {
          marginTop: 0,
          marginRight: 0,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px",
          "&:last-child": {
            paddingBottom: "24px",
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px 24px",
          gap: "8px",
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors?.grey100,
        },
      },
    },
    // ==============================|| INPUT OVERRIDES ||============================== //
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.textDark,
          "&::placeholder": {
            color: theme.darkTextSecondary,
            fontSize: "0.875rem",
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: inputBg,
          borderRadius: `${borderRadius}px`,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: inputBorder,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors?.primaryMain,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors?.primaryMain,
            borderWidth: "2px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors?.errorMain,
          },
        },
        input: {
          fontWeight: 500,
          backgroundColor: inputBg,
          // padding: "12px 14px", // TODO: Uncomment this if we want to keep the padding
          borderRadius: `${borderRadius}px`,
          color: theme.textDark,
          "&::placeholder": {
            color: theme.darkTextSecondary,
            opacity: 0.7,
          },
          "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${inputBg} inset`,
            WebkitTextFillColor: theme.textDark,
            caretColor: theme.textDark,
            borderRadius: `${borderRadius}px`,
          },
          "&.MuiInputBase-inputSizeSmall": {
            padding: "8px 12px",
            fontSize: "0.875rem",
          },
        },
        inputAdornedStart: {
          paddingLeft: 4,
        },
        notchedOutline: {
          borderRadius: `${borderRadius}px`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.darkTextSecondary,
          "&.Mui-focused": {
            color: theme.colors?.primaryMain,
          },
          "&.Mui-disabled": {
            color: theme.darkTextSecondary,
            opacity: 0.6,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.darkTextSecondary,
        },
      },
    },

    // ==============================|| DIVIDER OVERRIDES ||============================== //
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.divider,
          opacity: 1,
        },
      },
    },

    // ==============================|| AVATAR OVERRIDES ||============================== //
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: theme.colors?.primaryDark,
          background: theme.colors?.primary200,
          fontWeight: 600,
        },
      },
    },

    // ==============================|| CHIP OVERRIDES ||============================== //
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "100px",
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 600,
          fontSize: "11.5px",
          letterSpacing: "0.04em",
          "&.MuiChip-deletable .MuiChip-deleteIcon": {
            color: "inherit",
          },
        },
      },
    },
    // ==============================|| LINEAR PROGRESS OVERRIDES ||============================== //
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          height: 10,
        },
        bar: {
          borderRadius: "6px",
        },
      },
    },

    // ==============================|| TOOLTIP OVERRIDES ||============================== //
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          bgcolor: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
          color: isDark ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.9)",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: `${borderRadius}px`,
        },
      },
    },

    // ==============================|| ALERT OVERRIDES ||============================== //
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: `${borderRadius}px`,
          "& .MuiAlert-icon": {
            fontSize: "1.25rem",
          },
        },
        standardSuccess: {
          backgroundColor: theme.colors?.successLight,
          color: theme.colors?.successDark,
          "& .MuiAlert-icon": {
            color: theme.colors?.successMain,
          },
        },
        standardError: {
          backgroundColor: theme.colors?.errorLight,
          color: theme.colors?.errorDark,
          "& .MuiAlert-icon": {
            color: theme.colors?.errorMain,
          },
        },
        standardWarning: {
          backgroundColor: theme.colors?.warningLight,
          color: theme.colors?.warningDark,
          "& .MuiAlert-icon": {
            color: theme.colors?.warningMain,
          },
        },
        standardInfo: {
          backgroundColor: theme.colors?.infoLight,
          color: theme.colors?.infoDark,
          "& .MuiAlert-icon": {
            color: theme.colors?.infoMain,
          },
        },
      },
    },

    // ==============================|| POPOVER & MENU OVERRIDES ||============================== //
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          borderRadius: `${borderRadius}px`,
          border: `1px solid ${theme.divider}`,
          backgroundColor: theme.paper,
          zIndex: 1400,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          borderRadius: `${borderRadius}px`,
          border: `1px solid ${theme.divider}`,
          backgroundColor: theme.paper,
          zIndex: 1400,
        },
      },
    },

    // ==============================|| DIALOG OVERRIDES ||============================== //
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: `${borderRadius}px`,
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.5rem",
          fontWeight: 600,
          color: theme.heading,
          padding: "24px 24px 16px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0 24px 24px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px 24px",
          gap: "8px",
        },
      },
    },
    // ==============================|| TABLE OVERRIDES ||============================== //
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: "bold",
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.02)",
            borderBottom: `2px solid ${theme.divider}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.divider}`,
          "&:nth-of-type(even)": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.02)",
          },
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "4px 8px", // or whatever padding you prefer
        },
      },
    },
  };

  return componentOverrides;
}

/**
 * Export reusable component styles separately
 * These are used via theme.custom.componentStyles in components
 */
export function getReusableComponentStyles(theme) {
  const borderRadius = theme?.customization?.borderRadius || 6;

  return {
    mainContent: {
      // backgroundColor: theme.backgroundDefault,
      width: "100%",
      minHeight: "calc(100vh - 80px)",
      flexGrow: 1,
      padding: "20px",
      marginTop: "80px",
      marginRight: "20px",
    },
    menuCaption: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.heading,
      padding: "6px",
      textTransform: "capitalize",
      marginTop: "10px",
    },
    subMenuCaption: {
      fontSize: "0.6875rem",
      fontWeight: 500,
      color: theme.darkTextSecondary,
      textTransform: "capitalize",
    },
    commonAvatar: {
      cursor: "pointer",
      borderRadius: "8px",
    },
    smallAvatar: {
      width: "22px",
      height: "22px",
      fontSize: "1rem",
    },
    mediumAvatar: {
      width: "34px",
      height: "34px",
      fontSize: "1.2rem",
    },
    largeAvatar: {
      width: "44px",
      height: "44px",
      fontSize: "1.5rem",
    },
    xlargeAvatar: {
      width: "56px",
      height: "56px",
      fontSize: "1.75rem",
    },
    customInput: {
      marginTop: 1,
      marginBottom: 1,
      "& > label": {
        top: 23,
        left: 0,
        color: theme.colors?.grey500,
        '&[data-shrink="false"]': {
          top: 5,
        },
      },
      "& > div > input": {
        padding: "30.5px 14px 11.5px !important",
      },
      "& legend": {
        display: "none",
      },
      "& fieldset": {
        top: 0,
      },
    },
    formControl: {
      marginBottom: "16px",
      "& .MuiFormLabel-root": {
        color: theme.darkTextSecondary,
      },
      "& .MuiInputBase-root": {
        backgroundColor: theme.colors?.grey50,
        borderRadius: `${borderRadius}px`,
      },
    },
    statusChip: {
      fontWeight: 500,
      fontSize: "0.75rem",
      height: "24px",
      "&.success": {
        backgroundColor: theme.colors?.successLight,
        color: theme.colors?.successDark,
      },
      "&.error": {
        backgroundColor: theme.colors?.errorLight,
        color: theme.colors?.errorDark,
      },
      "&.warning": {
        backgroundColor: theme.colors?.warningLight,
        color: theme.colors?.warningDark,
      },
      "&.info": {
        backgroundColor: theme.colors?.infoLight,
        color: theme.colors?.infoDark,
      },
    },
    sectionDivider: {
      margin: "24px 0",
      borderColor: theme.divider,
    },
    lightDivider: {
      borderColor: theme.colors?.grey200,
      opacity: 0.5,
    },
  };
}
